import { useCallback, useState } from "react"
import { toast } from "react-toastify"
import {
  Filter,
  getFilterOperators,
  normalizeFilterGroups,
  normalizeGates,
  setUniformGate,
  type FilterFieldType,
  type FilterGate,
} from "~/utils/filter"
import { useFilterFields } from "./useFilterFields"
import { useFilters } from "./useFilters"

interface IncompleteFilter {
  id: string
  field?: string
  operator?: string
  gate?: FilterGate
}

interface UseFilterOperationsProps {
  filters: Filter[]
  onFiltersChange: (filters: Filter[]) => void
  filterFields?: FilterFieldType[]
  filterFieldOptions?: { label: string; value: string | undefined }[]
  /** Context-specific fields appended to the defaults (e.g. the supply plan's SKU Size). */
  extraFilterFields?: FilterFieldType[]
}

export function useFilterOperations({
  filters,
  onFiltersChange,
  filterFields: customFilterFields,
  filterFieldOptions: customFilterFieldOptions,
  extraFilterFields,
}: UseFilterOperationsProps) {
  const [incompleteFilters, setIncompleteFilters] = useState<IncompleteFilter[]>([])
  const { filterFieldOptions: defaultFieldOptions, allFilterFields } = useFilterFields(
    "",
    extraFilterFields,
  )
  const filterFieldOptions = customFilterFieldOptions ?? defaultFieldOptions
  const filterFields = customFilterFields ?? allFilterFields
  const totalFilterCount = filters.length + incompleteFilters.length

  const checkForDuplicateFilter = useCallback(
    (candidate: Filter): boolean => {
      const existingFilter = filters?.find(
        (f) =>
          f.field === candidate.field &&
          f.operator === candidate.operator &&
          f.value === candidate.value &&
          f.values.length === candidate.values.length &&
          f.values.every((val, i) => val === candidate.values[i]),
      )
      if (existingFilter) {
        toast.error("This exact filter already exists.")
        return true
      }
      return false
    },
    [filters],
  )

  const addFilter = useCallback(
    (newFilter: Filter) => {
      if (checkForDuplicateFilter(newFilter)) {
        return
      }

      const existing = filters ?? []
      const all = [...existing, newFilter]
      const existingGrouped = existing.length > 0 && existing.every((f) => f.groupId != null)
      const wantOr = existingGrouped || (newFilter.gate ?? "and") === "or"
      const next =
        all.length >= 2 && wantOr
          ? all.map((f) => ({ ...f, groupId: 1, gate: undefined }))
          : all.map((f) => ({ ...f, groupId: undefined }))
      onFiltersChange(normalizeGates(next))
    },
    [filters, onFiltersChange, checkForDuplicateFilter],
  )

  const updateFilter = useCallback(
    (filterToUpdate: Filter, updatedFilter: Filter, index?: number) => {
      const updatedFilters = filters.map((f, i) =>
        (
          index != null
            ? i === index
            : f.field === filterToUpdate.field && f.operator === filterToUpdate.operator
        )
          ? updatedFilter
          : f,
      )
      onFiltersChange(updatedFilters)
    },
    [filters, onFiltersChange],
  )

  const removeFilter = useCallback(
    (filterToRemove: Filter, index?: number) => {
      const updatedFilters =
        index != null
          ? filters.filter((_, i) => i !== index)
          : filters.filter((f) => f.field !== filterToRemove.field)
      onFiltersChange(updatedFilters)
    },
    [filters, onFiltersChange],
  )

  const clearAllLocallyStoredFilters = useCallback(() => {
    onFiltersChange([])
    setIncompleteFilters([])
  }, [onFiltersChange])

  const { clearAll } = useFilters()

  const clearAllFilters = useCallback(() => {
    clearAll()
  }, [clearAll])

  const handleFilterArrayValuesChange = useCallback(
    (filterToUpdate: Filter, newValues: string[], index?: number) => {
      const updatedFilter: Filter = {
        field: filterToUpdate.field,
        operator: filterToUpdate.operator,
        value: "",
        values: newValues,
        by: filterToUpdate.by,
        gate: filterToUpdate.gate,
        groupId: filterToUpdate.groupId,
      }

      updateFilter(filterToUpdate, updatedFilter, index)
    },
    [updateFilter],
  )

  const handleFilterSingleValueChange = useCallback(
    (filterToUpdate: Filter, newValue: string, index?: number) => {
      const updatedFilter: Filter = {
        field: filterToUpdate.field,
        operator: filterToUpdate.operator,
        value: newValue,
        values: [],
        by: filterToUpdate.by,
        gate: filterToUpdate.gate,
        groupId: filterToUpdate.groupId,
      }

      updateFilter(filterToUpdate, updatedFilter, index)
    },
    [updateFilter],
  )

  const handleFilterGateChange = useCallback(
    (filterToUpdate: Filter, gate: FilterGate, index?: number) => {
      const idx = index ?? filters.indexOf(filterToUpdate)
      if (idx < 0) {
        updateFilter(filterToUpdate, { ...filterToUpdate, gate }, index)
        return
      }
      onFiltersChange(setUniformGate(filters, idx, gate))
    },
    [filters, onFiltersChange, updateFilter],
  )

  const handleFilterGroupChange = useCallback(
    (filterToUpdate: Filter, groupId: number | undefined, index?: number) => {
      const updatedFilters = filters.map((f, i) =>
        (
          index != null
            ? i === index
            : f.field === filterToUpdate.field && f.operator === filterToUpdate.operator
        )
          ? { ...f, groupId, gate: groupId != null ? undefined : f.gate }
          : f,
      )
      onFiltersChange(normalizeGates(normalizeFilterGroups(updatedFilters)))
    },
    [filters, onFiltersChange],
  )

  const handleAddIncompleteFilter = useCallback((gate?: FilterGate, field?: string) => {
    const newIncompleteFilter = {
      id: `incomplete-${Date.now()}`,
      gate,
      field,
    }
    setIncompleteFilters((prev) => [...prev, newIncompleteFilter])
  }, [])

  const handleIncompleteFilterGateChange = useCallback(
    (incompleteFilterId: string, gate: FilterGate) => {
      setIncompleteFilters((prev) =>
        prev.map((f) => (f.id === incompleteFilterId ? { ...f, gate } : f)),
      )
    },
    [],
  )

  const handleAllIncompleteGatesChange = useCallback((gate: FilterGate) => {
    setIncompleteFilters((prev) => prev.map((f) => ({ ...f, gate })))
  }, [])

  const handleIncompleteFilterFieldChange = useCallback(
    (incompleteFilterId: string, fieldValue: string) => {
      setIncompleteFilters((prev) =>
        prev.map((f) =>
          f.id === incompleteFilterId ? { ...f, field: fieldValue, operator: undefined } : f,
        ),
      )
    },
    [],
  )

  const handleIncompleteFilterOperatorChange = useCallback(
    (incompleteFilterId: string, operatorValue: string) => {
      const incompleteFilter = incompleteFilters.find((f) => f.id === incompleteFilterId)
      if (!incompleteFilter?.field) {
        setIncompleteFilters((prev) =>
          prev.map((f) => (f.id === incompleteFilterId ? { ...f, operator: operatorValue } : f)),
        )
        return
      }

      const operatorDef = getFilterOperators(incompleteFilter.field, filterFields).find(
        (op) => op.value === operatorValue,
      )

      if (operatorDef?.valueless) {
        addFilter({
          field: incompleteFilter.field,
          operator: operatorValue,
          value: "",
          values: [],
          by: "variantId",
          gate: incompleteFilter.gate,
        })
        setIncompleteFilters((prev) => prev.filter((f) => f.id !== incompleteFilterId))
        return
      }

      setIncompleteFilters((prev) =>
        prev.map((f) => (f.id === incompleteFilterId ? { ...f, operator: operatorValue } : f)),
      )
    },
    [incompleteFilters, filterFields, filterFieldOptions, addFilter, checkForDuplicateFilter],
  )

  const handleIncompleteFilterValueChange = useCallback(
    (incompleteFilterId: string, value: string) => {
      const incompleteFilter = incompleteFilters.find((f) => f.id === incompleteFilterId)
      if (!incompleteFilter?.field || !incompleteFilter?.operator) return

      const newFilter: Filter = {
        field: incompleteFilter.field,
        operator: incompleteFilter.operator,
        value: value,
        values: [],
        by: "variantId",
        gate: incompleteFilter.gate,
      }

      addFilter(newFilter)
      setIncompleteFilters((prev) => prev.filter((f) => f.id !== incompleteFilterId))
    },
    [incompleteFilters, addFilter, checkForDuplicateFilter, filterFieldOptions],
  )

  const handleIncompleteFilterArrayValueChange = useCallback(
    (incompleteFilterId: string, values: string[]) => {
      const incompleteFilter = incompleteFilters.find((f) => f.id === incompleteFilterId)
      if (!incompleteFilter?.field || !incompleteFilter?.operator) return

      const newFilter: Filter = {
        field: incompleteFilter.field,
        operator: incompleteFilter.operator,
        value: "",
        values: values,
        by: "variantId",
        gate: incompleteFilter.gate,
      }

      addFilter(newFilter)
      setIncompleteFilters((prev) => prev.filter((f) => f.id !== incompleteFilterId))
    },
    [incompleteFilters, filterFieldOptions, addFilter, checkForDuplicateFilter],
  )

  const handleRemoveIncompleteFilter = useCallback((incompleteFilterId: string) => {
    setIncompleteFilters((prev) => prev.filter((f) => f.id !== incompleteFilterId))
  }, [])

  const handleCompleteFilterFieldChange = useCallback(
    (filterToUpdate: Filter, newFieldValue: string, index?: number) => {
      const updatedFilters =
        index != null
          ? filters.filter((_, i) => i !== index)
          : filters.filter((f) => f.field !== filterToUpdate.field)
      onFiltersChange(updatedFilters)

      const newIncompleteFilter = {
        id: `incomplete-${Date.now()}`,
        field: newFieldValue,
        gate: filterToUpdate.gate,
      }
      setIncompleteFilters((prev) => [...prev, newIncompleteFilter])
    },
    [filters, onFiltersChange],
  )

  const handleCompleteFilterOperatorChange = useCallback(
    (filterToUpdate: Filter, newOperatorValue: string, index?: number) => {
      const operatorDef = getFilterOperators(filterToUpdate.field, filterFields).find(
        (op) => op.value === newOperatorValue,
      )
      const needsArrayValue = operatorDef?.arrayValue ?? false
      const valueless = operatorDef?.valueless ?? false

      const updatedFilter: Filter = {
        field: filterToUpdate.field,
        operator: newOperatorValue,
        value: valueless ? "" : needsArrayValue ? "" : filterToUpdate.value || "",
        values: valueless
          ? []
          : needsArrayValue
            ? filterToUpdate.values.length > 0
              ? filterToUpdate.values
              : []
            : [],
        by: filterToUpdate.by,
        gate: filterToUpdate.gate,
        groupId: filterToUpdate.groupId,
      }

      updateFilter(filterToUpdate, updatedFilter, index)
    },
    [updateFilter, filterFields],
  )

  return {
    incompleteFilters,
    totalFilterCount,
    filterFieldOptions,
    filterFields,
    addFilter,
    updateFilter,
    removeFilter,
    clearAllLocallyStoredFilters,
    clearAllFilters,
    checkForDuplicateFilter,
    handleFilterArrayValuesChange,
    handleFilterSingleValueChange,
    handleFilterGateChange,
    handleFilterGroupChange,
    handleAddIncompleteFilter,
    handleIncompleteFilterGateChange,
    handleAllIncompleteGatesChange,
    handleIncompleteFilterFieldChange,
    handleIncompleteFilterOperatorChange,
    handleIncompleteFilterValueChange,
    handleIncompleteFilterArrayValueChange,
    handleRemoveIncompleteFilter,
    handleCompleteFilterFieldChange,
    handleCompleteFilterOperatorChange,
  }
}
